<template>
  <EventTileBase
    :event="event"
    :showEdit="showEdit"
    :canEdit="canEditEvent"
    :showMenu="showMenu"
    :showPinnedActions="showPinnedActions"
    :showLeagueLogo="showLeagueLogo"
    :showTeamLogos="showTeamLogos"
    @navigate="emit('navigate'); router.push(`/event/${event._id}`)"
    @unpinEvent="emit('unpinEvent', $event)"
    @deleteEvent="emit('deleteEvent', $event)"
    @cancelEvent="emit('cancelEvent', $event)"
    @editEvent="emit('editEvent', $event)"
    @menuOpen="emit('menuOpen')"
    @menuClose="emit('menuClose')"
  >
    <!-- Row 3: players + goalies badges -->
    <template #detail>
      <div class="training-tile-detail">
        <span class="training-tile-badge" :class="playersFull ? 'badge-warn' : 'badge-ok'">
          <span class="training-tile-badge-label">{{ $t('playersShort') }}:</span>
          {{ playersLabel }}
        </span>
        <span class="training-tile-badge" :class="goaliesFull ? 'badge-warn' : 'badge-ok'">
          <span class="training-tile-badge-label">{{ $t('goaliesShort') }}:</span>
          {{ goaliesLabel }}
        </span>
      </div>
    </template>
  </EventTileBase>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useRouter } from 'vue-router'
import type { IEvent } from '@/types'
import EventTileBase from '@/components/EventTiles/EventTileBase.vue'
import { useEventPermissions } from '@/composables/useEventPermissions'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps<{
  event: IEvent
  fullDate?: boolean
  showEdit: boolean
  showMenu?: boolean
  showPinnedActions?: boolean
  showLeagueLogo?: boolean
  showTeamLogos?: boolean
  canEdit?: boolean | null
}>()

const router = useRouter()
const emit = defineEmits(['unpinEvent', 'deleteEvent', 'cancelEvent', 'editEvent', 'menuOpen', 'menuClose', 'navigate'])

const { canEditEvent } = useEventPermissions(toRef(props, 'event'), 'league-admin')

/* === attendance pills === */
const playersCount = computed(() => props.event.attendants?.players?.length ?? 0)
const goaliesCount = computed(() => props.event.attendants?.goalies?.length ?? 0)
const playersMax = computed(() => props.event.meta?.players ?? 0)
const goaliesMax = computed(() => props.event.meta?.goalies ?? 0)

const playersFull = computed(() => playersMax.value > 0 && playersCount.value >= playersMax.value)
const goaliesFull = computed(() => goaliesMax.value > 0 && goaliesCount.value >= goaliesMax.value)
const playersLabel = computed(() =>
  playersFull.value ? t('eventTile.reserves') : `${playersCount.value}/${playersMax.value}`
)
const goaliesLabel = computed(() =>
  goaliesFull.value ? t('eventTile.reserves') : `${goaliesCount.value}/${goaliesMax.value}`
)
</script>

<style lang="scss" scoped>
.training-tile-detail {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.training-tile-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 7px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.training-tile-badge-label {
  opacity: 0.85;
}

.badge-ok {
  background: #4caf50;
  color: #fff;
}

.badge-warn {
  background: #ef5350;
  color: #fff;
}
</style>
